/* ===========================================================================
   Orly PDP gallery — two jobs:
   1. MOBILE carousel dot sync (the scroll-snap track is pure CSS; JS only
      lights the correct dot as you swipe, and lets a dot tap jump to a slide).
   2. LIGHTBOX (mobile + desktop): tapping any gallery image opens a fullscreen
      overlay with prev/next arrows, keyboard arrows, swipe, ESC, click-outside,
      and a close button. Accessible (focus trap + restore, aria-labels) and
      respects prefers-reduced-motion.
   Vanilla JS, no dependencies. Self-contained; safe to defer.
   =========================================================================== */
(function () {
  'use strict';

  var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function init(gallery) {
    if (!gallery || gallery.__orlyGalleryInit) return;
    gallery.__orlyGalleryInit = true;

    var track = gallery.querySelector('[data-orly-gallery-track]');
    var slides = Array.prototype.slice.call(gallery.querySelectorAll('[data-orly-gallery-slide]'));
    var dotsWrap = gallery.querySelector('[data-orly-gallery-dots]');
    var dots = dotsWrap ? Array.prototype.slice.call(dotsWrap.querySelectorAll('[data-dot]')) : [];
    if (!slides.length) return;

    /* ---- 1. dot sync (mobile carousel) ---- */
    function activeIndexFromScroll() {
      if (!track) return 0;
      var w = track.clientWidth || 1;
      return Math.round(track.scrollLeft / w);
    }
    function syncDots() {
      var i = activeIndexFromScroll();
      dots.forEach(function (d, di) { d.classList.toggle('is-active', di === i); });
    }
    if (track && dots.length) {
      var raf;
      track.addEventListener('scroll', function () {
        if (raf) return;
        raf = requestAnimationFrame(function () { raf = null; syncDots(); });
      }, { passive: true });
      dots.forEach(function (d, di) {
        d.addEventListener('click', function () {
          var w = track.clientWidth || 1;
          track.scrollTo({ left: di * w, behavior: REDUCED ? 'auto' : 'smooth' });
        });
      });
    }

    /* ---- 2. lightbox ---- */
    var lb = buildLightbox();
    var current = 0;
    var lastFocused = null;

    function imageFor(i) {
      var s = slides[i];
      // Reuse the already-loaded thumbnail src for an instant paint, then let
      // srcset upgrade to full resolution — avoids a blank flash on open.
      var thumb = (s.querySelector('img') || {}).currentSrc || (s.querySelector('img') || {}).src;
      return { full: s.getAttribute('data-full'), srcset: s.getAttribute('data-full-srcset'), thumb: thumb };
    }

    function render(i) {
      current = (i + slides.length) % slides.length;
      var d = imageFor(current);
      lb.img.removeAttribute('sizes');
      lb.img.setAttribute('sizes', '100vw');
      if (d.srcset) lb.img.setAttribute('srcset', d.srcset); else lb.img.removeAttribute('srcset');
      lb.img.src = d.thumb || d.full;
      lb.img.alt = (slides[current].querySelector('img') || {}).alt || 'Product image';
      lb.counter.textContent = (current + 1) + ' / ' + slides.length;
      var multi = slides.length > 1;
      lb.prev.style.display = multi ? '' : 'none';
      lb.next.style.display = multi ? '' : 'none';
    }

    function open(i) {
      lastFocused = document.activeElement;
      render(i);
      document.body.appendChild(lb.root);
      // force reflow so the transition runs
      void lb.root.offsetWidth;
      lb.root.classList.add('is-open');
      document.documentElement.classList.add('orly-lightbox-lock');
      document.addEventListener('keydown', onKey);
      lb.close.focus();
    }

    function close() {
      lb.root.classList.remove('is-open');
      document.documentElement.classList.remove('orly-lightbox-lock');
      document.removeEventListener('keydown', onKey);
      var remove = function () { if (lb.root.parentNode) lb.root.parentNode.removeChild(lb.root); };
      if (REDUCED) remove(); else setTimeout(remove, 220);
      if (lastFocused && lastFocused.focus) lastFocused.focus();
    }

    function onKey(e) {
      if (e.key === 'Escape') { close(); }
      else if (e.key === 'ArrowRight') { render(current + 1); }
      else if (e.key === 'ArrowLeft') { render(current - 1); }
      else if (e.key === 'Tab') {
        // simple focus trap among the lightbox controls
        var f = [lb.close, lb.prev, lb.next].filter(function (el) { return el.style.display !== 'none'; });
        var idx = f.indexOf(document.activeElement);
        if (e.shiftKey && idx <= 0) { e.preventDefault(); f[f.length - 1].focus(); }
        else if (!e.shiftKey && idx === f.length - 1) { e.preventDefault(); f[0].focus(); }
      }
    }

    // open on slide click/tap
    slides.forEach(function (s, i) {
      s.addEventListener('click', function (e) { e.preventDefault(); open(i); });
    });

    // lightbox controls
    lb.close.addEventListener('click', close);
    lb.prev.addEventListener('click', function () { render(current - 1); });
    lb.next.addEventListener('click', function () { render(current + 1); });
    lb.root.addEventListener('click', function (e) {
      // Anything that isn't the image or a control = click-outside -> close.
      // (The stage has pointer-events:none, so empty-margin taps land on root.)
      if (e.target === lb.root || e.target === lb.stage) close();
    });

    // swipe on the image itself (the stage is pointer-events:none)
    var sx = 0, sy = 0, swiping = false;
    lb.img.addEventListener('touchstart', function (e) {
      if (e.touches.length !== 1) return;
      sx = e.touches[0].clientX; sy = e.touches[0].clientY; swiping = true;
    }, { passive: true });
    lb.img.addEventListener('touchend', function (e) {
      if (!swiping) return; swiping = false;
      var dx = (e.changedTouches[0].clientX - sx);
      var dy = (e.changedTouches[0].clientY - sy);
      if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy)) {
        render(dx < 0 ? current + 1 : current - 1);
      }
    }, { passive: true });

    if (dots.length) syncDots();
  }

  function buildLightbox() {
    var root = document.createElement('div');
    root.className = 'orly-lightbox';
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.setAttribute('aria-label', 'Product image viewer');

    var stage = document.createElement('div');
    stage.className = 'orly-lightbox__stage';

    var img = document.createElement('img');
    img.className = 'orly-lightbox__img';
    img.decoding = 'async';
    stage.appendChild(img);

    var close = ctrl('orly-lightbox__close', 'Close image viewer',
      '<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="1.8" fill="none" stroke-linecap="round"/></svg>');
    var prev = ctrl('orly-lightbox__nav orly-lightbox__nav--prev', 'Previous image',
      '<svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true"><path d="M15 5l-7 7 7 7" stroke="currentColor" stroke-width="1.8" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>');
    var next = ctrl('orly-lightbox__nav orly-lightbox__nav--next', 'Next image',
      '<svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true"><path d="M9 5l7 7-7 7" stroke="currentColor" stroke-width="1.8" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>');

    var counter = document.createElement('p');
    counter.className = 'orly-lightbox__counter';

    root.appendChild(stage);
    root.appendChild(close);
    root.appendChild(prev);
    root.appendChild(next);
    root.appendChild(counter);

    return { root: root, stage: stage, img: img, close: close, prev: prev, next: next, counter: counter };
  }

  function ctrl(cls, label, svg) {
    var b = document.createElement('button');
    b.type = 'button';
    b.className = cls;
    b.setAttribute('aria-label', label);
    b.innerHTML = svg;
    return b;
  }

  function boot() {
    document.querySelectorAll('[data-orly-gallery]').forEach(init);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
